import React from "react";
import { Database, AlertTriangle, Download, Trash2, Archive, Shield, CheckCircle, ArrowUpDown } from "lucide-react";
import html2pdf from 'html2pdf.js';

export default function DatabaseAdminGuide() {
  const handleDownloadPDF = () => {
    // Hide the button before generating PDF
    const button = document.querySelector('.download-button-container');
    if (button) button.style.display = 'none';
    
    const element = document.getElementById('guide-content');
    const opt = {
      margin: 0.5,
      filename: 'Golf-Eclectic-Database-Admin-Guide.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    };
    
    html2pdf().set(opt).from(element).save().then(() => {
      // Show button again after PDF is generated
      if (button) button.style.display = 'block';
    });
  };

  return (
    <div id="guide-content" className="max-w-4xl mx-auto space-y-8">
      {/* Welcome Section with Download Button */}
      <div className="text-center space-y-2">
        {/* Golf Eclectic Logo - Shows everywhere */}
        <img 
          src="/logo.png" 
          alt="Golf Eclectic Logo" 
          className="w-24 h-24 mx-auto rounded-full object-cover"
        />
        <h1 className="text-xl font-semibold text-ink tracking-tight">Database Admin Guide</h1>
        <p className="text-lg text-ink-muted">Advanced tools for database management and maintenance</p>
        
        {/* Download Button - Hidden in PDF */}
        <div className="download-button-container">
          <button
            onClick={handleDownloadPDF}
            className="mt-4 inline-flex items-center px-6 py-3 bg-ink hover:bg-ink-hover text-white font-semibold rounded-lg shadow-md transition-colors"
          >
            <Download className="w-5 h-5 mr-2" />
            Download as PDF
          </button>
        </div>
      </div>

      {/* Warning Section */}
      <section className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle className="w-8 h-8 text-red-600" />
          <h2 className="text-2xl font-bold text-red-900">Important Warning</h2>
        </div>
        <p className="text-red-800 font-semibold mb-2">
          Database utilities perform powerful operations that can permanently modify or delete data.
        </p>
        <ul className="space-y-1 text-red-700">
          <li>• Always <span className="font-bold">back up your database</span> before using these tools</li>
          <li>• Some operations <span className="font-bold">cannot be undone</span></li>
          <li>• Test on a backup/development environment first if possible</li>
          <li>• Double-check confirmations before proceeding</li>
        </ul>
      </section>

      {/* Access Requirements */}
      <section className="bg-accent-tint-2 border border-line rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-accent" />
          Access Requirements
        </h2>
        <p className="text-ink-muted mb-2">Database Utilities are only accessible to:</p>
        <ul className="space-y-1 text-ink-muted">
          <li>• <span className="font-semibold">Admin accounts</span> with proper credentials</li>
          <li>• Navigate to: <span className="font-mono bg-surface-2 px-2 py-1 rounded">Admin Menu → Utilities</span></li>
        </ul>
      </section>

      {/* Renumber Rounds */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <ArrowUpDown className="w-6 h-6 text-accent" />
          Renumber Rounds by Date
        </h2>
        
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What It Does</h3>
            <p className="text-ink-muted mb-2">
              Renumbers all rounds across all competitions in chronological order by date:
            </p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Round 1 = Earliest date</li>
              <li>• Round 2 = Next date</li>
              <li>• Round 3 = Next date, etc.</li>
              <li>• Processes all player-competition combinations</li>
            </ul>
          </div>

          <div className="bg-accent-tint-2 border border-accent/30 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">✅ When to Use</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• After importing historical data</li>
              <li>• When rounds were entered out of chronological order</li>
              <li>• To fix inconsistent round numbering</li>
              <li>• After bulk data corrections</li>
            </ul>
          </div>

          <div className="bg-app border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">🔧 How to Use</h3>
            <ol className="space-y-1 text-ink-muted list-decimal list-inside">
              <li>Click "Renumber All Rounds" button</li>
              <li>Confirm the operation</li>
              <li>System processes all rounds and updates numbering</li>
              <li>Success message shows count of combinations processed</li>
            </ol>
          </div>

          <div className="bg-app border border-line-strong rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">ℹ️ Safety Notes</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <span className="font-semibold">Safe operation:</span> Only updates round_number field</li>
              <li>• Does NOT modify scores, dates, or other data</li>
              <li>• Can be run multiple times safely</li>
              <li>• Automatic: New rounds are renumbered on submission</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Delete Users */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Trash2 className="w-6 h-6 text-red-600" />
          Delete Users from System
        </h2>
        
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What It Does</h3>
            <p className="text-ink-muted mb-2">
              Permanently deletes users and <span className="font-bold text-red-600">ALL</span> their associated data:
            </p>
            <ul className="space-y-1 text-ink-muted">
              <li>• User account (Google OAuth or Email/Password)</li>
              <li>• Player records (all competition associations)</li>
              <li>• All rounds and scores</li>
              <li>• All best scores (eclectic data)</li>
              <li>• User sessions</li>
              <li>• Message preferences</li>
            </ul>
          </div>

          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-red-900 mb-2">⚠️ CRITICAL WARNING</h3>
            <ul className="space-y-2 text-red-800">
              <li>• <span className="font-bold">CANNOT BE UNDONE</span></li>
              <li>• All user data is permanently lost</li>
              <li>• User cannot be recovered after deletion</li>
              <li>• Backup database before deleting users</li>
            </ul>
          </div>

          <div className="bg-app border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">🔧 How to Use</h3>
            <ol className="space-y-2 text-ink-muted list-decimal list-inside">
              <li>Click "Load All Users" button</li>
              <li>Review list of users (shows name, email, auth type, competition count)</li>
              <li>Click "Delete" button next to user</li>
              <li>Read warning dialog carefully</li>
              <li>Type "DELETE" (in capital letters) to confirm</li>
              <li>User and all data permanently removed</li>
            </ol>
          </div>

          <div className="bg-warm-tint/40 border border-warm/40 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">📝 User Types Displayed</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <span className="font-semibold">Email/Password:</span> Local accounts with passwords</li>
              <li>• <span className="font-semibold">Google:</span> Google OAuth authenticated users</li>
              <li>• Shows competition count for each user</li>
              <li>• Admin accounts are NOT shown (cannot delete admins)</li>
            </ul>
          </div>

          <div className="bg-accent-tint-2 border border-accent/30 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">✅ When to Use</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Remove test/duplicate accounts (or use Test Account Management for test accounts)</li>
              <li>• Delete inactive users who left the organization</li>
              <li>• Clean up accounts created in error</li>
              <li>• GDPR data deletion requests</li>
            </ul>
          </div>

          <div className="bg-accent-tint-2 border border-accent rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">🧪 Test Accounts</h3>
            <p className="text-ink mb-2">
              <strong>For test accounts</strong> (accounts flagged with is_test_account), use the dedicated <strong>Test Account Management System</strong> in Admin Utilities instead:
            </p>
            <ul className="space-y-1 text-ink">
              <li>• Safer reset options (preserves credentials)</li>
              <li>• Bulk operations (reset all test accounts at once)</li>
              <li>• Production testing features</li>
              <li>• Complete data isolation management</li>
            </ul>
          </div>

          <div className="bg-app border border-line-strong rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">ℹ️ Alternative: Remove from Competition</h3>
            <p className="text-ink-muted mb-2">
              If you want to remove a user from ONE competition without deleting them entirely:
            </p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Go to Competition View → Player Management</li>
              <li>• Click "Remove" next to player name</li>
              <li>• User stays in system but removed from that competition</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Backup Database */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Download className="w-6 h-6 text-accent-deep" />
          Backup Database
        </h2>
        
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What It Does</h3>
            <p className="text-ink-muted">
              Downloads a complete JSON backup of all database collections:
            </p>
            <ul className="space-y-1 text-ink-muted mt-2">
              <li>• Users and sessions</li>
              <li>• Competitions</li>
              <li>• Players</li>
              <li>• Rounds and scores</li>
              <li>• Best scores (eclectic)</li>
              <li>• Messages and preferences</li>
            </ul>
          </div>

          <div className="bg-accent-tint-2 border border-accent/30 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">💾 Best Practices</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <span className="font-semibold">Backup regularly:</span> Weekly or before major changes</li>
              <li>• <span className="font-semibold">Before deletions:</span> Always backup before using Delete Users</li>
              <li>• <span className="font-semibold">Store securely:</span> Keep backups in safe location</li>
              <li>• <span className="font-semibold">Label clearly:</span> Include date in filename</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Restore Database */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          Restore Database
        </h2>
        
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-300 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-red-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              ⚠️ CRITICAL WARNING - DESTRUCTIVE OPERATION
            </h3>
            <p className="text-red-800 font-semibold mb-2">
              Restoring a database backup will COMPLETELY REPLACE ALL current data.
            </p>
            <ul className="space-y-1 text-red-700">
              <li>• All current users, competitions, scores will be DELETED</li>
              <li>• ALL data will be replaced with the backup file contents</li>
              <li>• This action CANNOT BE UNDONE</li>
              <li>• You will lose everything added since the backup was created</li>
            </ul>
          </div>

          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What It Does</h3>
            <p className="text-ink-muted mb-2">
              Uploads a backup JSON file and restores your entire database:
            </p>
            <ol className="space-y-1 text-ink-muted list-decimal ml-5">
              <li>Deletes ALL current data from all collections</li>
              <li>Inserts all data from the backup file</li>
              <li>Shows statistics of deleted and restored records</li>
              <li>Automatically refreshes the application</li>
            </ol>
          </div>

          <div className="bg-warm-tint/40 border border-warm/40 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">📋 When to Use Restore</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <span className="font-semibold">Disaster recovery:</span> System failure or data corruption</li>
              <li>• <span className="font-semibold">Rollback:</span> Undo major changes that went wrong</li>
              <li>• <span className="font-semibold">Testing recovery:</span> Practice restore procedures</li>
              <li>• <span className="font-semibold">Migration:</span> Move data between environments</li>
            </ul>
          </div>

          <div className="bg-app border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">✅ Restore Procedure</h3>
            <ol className="space-y-2 text-ink-muted list-decimal ml-5">
              <li><span className="font-semibold">Download current backup FIRST</span> - Before restoring, always download a backup of current state</li>
              <li><span className="font-semibold">Prepare backup file</span> - Locate the backup JSON file you want to restore</li>
              <li><span className="font-semibold">Verify backup file</span> - Ensure it's the correct backup with proper date/time</li>
              <li><span className="font-semibold">Click "Select Backup File to Restore"</span> - Upload button in red card</li>
              <li><span className="font-semibold">Select your backup file</span> - Choose the JSON file from your computer</li>
              <li><span className="font-semibold">Confirm the warning</span> - Read and confirm the destructive operation warning</li>
              <li><span className="font-semibold">Wait for completion</span> - Process may take 1-2 minutes</li>
              <li><span className="font-semibold">Application refreshes</span> - Page reloads automatically with restored data</li>
            </ol>
          </div>

          <div className="bg-surface-2 border border-line-strong rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">🔒 Safety Tips</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <span className="font-semibold text-red-600">ALWAYS backup current data first</span> before restoring</li>
              <li>• <span className="font-semibold">Test on development environment</span> if possible</li>
              <li>• <span className="font-semibold">Verify backup file integrity</span> before uploading</li>
              <li>• <span className="font-semibold">Communicate with users</span> before restoring in production</li>
              <li>• <span className="font-semibold">Have rollback plan</span> - Keep multiple backup versions</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Remove Orphaned Data */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Trash2 className="w-6 h-6 text-warm" />
          Remove Orphaned Data
        </h2>
        
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What It Does</h3>
            <p className="text-ink-muted mb-2">
              Cleans up "orphaned" records with broken references:
            </p>
            <ul className="space-y-1 text-ink-muted">
              <li>• <span className="font-semibold">Orphaned Players:</span> Player records for deleted users or competitions</li>
              <li>• <span className="font-semibold">Orphaned Rounds:</span> Rounds for deleted players or competitions</li>
              <li>• <span className="font-semibold">Orphaned Best Scores:</span> Best scores for deleted players or competitions</li>
            </ul>
          </div>

          <div className="bg-accent-tint-2 border border-accent/30 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">✅ When to Use</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• After deleting users or competitions</li>
              <li>• After importing/restoring data</li>
              <li>• When database integrity check shows orphaned data</li>
              <li>• Periodic maintenance (monthly)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Archive Competitions */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <Archive className="w-6 h-6 text-accent-deep" />
          Archive Old Competitions
        </h2>
        
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What It Does</h3>
            <p className="text-ink-muted">
              Marks old competitions as archived, making them read-only and hidden from active lists.
            </p>
          </div>

          <div className="bg-app border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">📅 Archive Criteria</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Competition end date is more than 6 months ago</li>
              <li>• No recent activity (no rounds in last 3 months)</li>
              <li>• Keeps all data intact (scores, rounds, etc.)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Integrity Check */}
      <section>
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-accent-deep" />
          Database Integrity Check
        </h2>
        
        <div className="space-y-4">
          <div className="bg-white border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">What It Does</h3>
            <p className="text-ink-muted mb-2">
              Scans the entire database for issues:
            </p>
            <ul className="space-y-1 text-ink-muted">
              <li>• Orphaned players, rounds, and scores</li>
              <li>• Duplicate rounds (same player, date, competition)</li>
              <li>• Invalid dates (future dates, dates outside competition range)</li>
              <li>• Score inconsistencies (scores > 10, negative scores)</li>
            </ul>
          </div>

          <div className="bg-accent-tint-2 border border-accent/30 rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">🔍 When to Use</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• Regular health checks (weekly/monthly)</li>
              <li>• After data imports or migrations</li>
              <li>• When users report data issues</li>
              <li>• Before major operations (renumbering, archiving)</li>
            </ul>
          </div>

          <div className="bg-app border border-line rounded-lg p-4">
            <h3 className="font-semibold text-lg text-ink mb-2">📊 Understanding Results</h3>
            <ul className="space-y-1 text-ink-muted">
              <li>• <span className="font-semibold text-accent-deep">0 issues:</span> Database is healthy</li>
              <li>• <span className="font-semibold text-warm">Minor issues:</span> Orphaned data (use cleanup)</li>
              <li>• <span className="font-semibold text-red-600">Major issues:</span> Data corruption (contact support)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Best Practices */}
      <section className="bg-app border border-line rounded-lg p-6">
        <h2 className="text-xl font-semibold text-ink tracking-tight mb-4">💡 Database Admin Best Practices</h2>
        <ul className="space-y-2 text-ink-muted">
          <li>• <span className="font-semibold">Backup first:</span> Always backup before major operations</li>
          <li>• <span className="font-semibold">Regular checks:</span> Run integrity check monthly</li>
          <li>• <span className="font-semibold">Clean periodically:</span> Remove orphaned data quarterly</li>
          <li>• <span className="font-semibold">Archive old data:</span> Keep active competitions list manageable</li>
          <li>• <span className="font-semibold">Document changes:</span> Note what operations were performed and when</li>
          <li>• <span className="font-semibold">Test first:</span> Try operations on test data when possible</li>
          <li>• <span className="font-semibold">Renumber after fixes:</span> Run renumber rounds after bulk corrections</li>
        </ul>
      </section>

      {/* Emergency Recovery */}
      <section className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-red-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          Emergency Recovery
        </h2>
        <p className="text-red-800 mb-3 font-semibold">
          If you accidentally delete important data:
        </p>
        <ol className="space-y-2 text-red-700 list-decimal list-inside">
          <li><span className="font-semibold">STOP:</span> Don't perform any more operations</li>
          <li><span className="font-semibold">Contact support immediately:</span> hello@golfeclectic.net</li>
          <li><span className="font-semibold">Provide details:</span> What was deleted, when, and any backup files</li>
          <li><span className="font-semibold">Restore from backup:</span> If you have a recent backup</li>
        </ol>
      </section>

      {/* Support */}
      <section className="text-center bg-app rounded-lg p-6">
        <h2 className="text-xl font-bold text-ink mb-2">Technical Support</h2>
        <p className="text-ink-muted">
          For database issues, recovery, or questions:
          <br />
          Email: <span className="font-semibold text-accent">hello@golfeclectic.net</span>
        </p>
      </section>
    </div>
  );
}